import clsx from "clsx";
import { useState, type HTMLAttributes, type ReactNode } from "react";
import type { P5ContentTone } from "./p5ContentPrimitives.tsx";
import { MAX_STAT_INSIGHTS_PER_PAGE } from "./statInsights.ts";

/**
 * C4 data-story renderer: each conclusion (computed by a statInsights.ts pure
 * function, never authored ad hoc) is ONE line inside a <details><summary>;
 * tapping it expands the evidence rows that back the conclusion. The evidence
 * body is lazy-mounted — closed <details> children still produce layout boxes
 * that the V1 first-screen audit would count (Roster/TeamStats/C1 precedent).
 *
 * At most MAX_STAT_INSIGHTS_PER_PAGE conclusions render per page, and a page
 * with zero qualifying conclusions must not render this block at all (the A3
 * no-meaningless-module precedent) — callers guard, this component enforces.
 */

export type MobileStatInsightItem = {
  /** One line, entity + numeric text first; no narration sentences. */
  conclusion: ReactNode;
  /** Evidence rows (leaderboard rows / rank rows) shown when expanded. */
  evidence: ReactNode;
  id: string;
  tone?: P5ContentTone;
};

type MobileStatInsightsProps = HTMLAttributes<HTMLElement> & {
  items: readonly MobileStatInsightItem[];
};

export const MobileStatInsights = ({
  className,
  items,
  ...sectionProps
}: MobileStatInsightsProps) => {
  const [openIds, setOpenIds] = useState<Record<string, boolean>>({});
  const visibleItems = items.slice(0, MAX_STAT_INSIGHTS_PER_PAGE);

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <section
      className={clsx("mobile-stat-insights", className)}
      data-c4-stat-insights=""
      data-c4-stat-insight-count={visibleItems.length}
      {...sectionProps}
    >
      {visibleItems.map((item) => (
        <details
          className="mobile-stat-insight"
          data-c4-stat-insight={item.id}
          data-c4-stat-insight-tone={item.tone ?? "neutral"}
          key={item.id}
          onToggle={(event) => {
            const open = (event.currentTarget as HTMLDetailsElement).open;
            setOpenIds((previous) => ({ ...previous, [item.id]: open }));
          }}
        >
          <summary className="mobile-stat-insight-summary">
            <span className="mobile-stat-insight-conclusion">{item.conclusion}</span>
          </summary>
          {openIds[item.id] ? (
            <div className="mobile-stat-insight-evidence" data-c4-stat-insight-evidence="">
              {item.evidence}
            </div>
          ) : null}
        </details>
      ))}
    </section>
  );
};
